import { DeltaPacket } from '@/stores/realtime';

import { EntityWithId, mergeEntity, removeEntityById, upsertEntityById } from './collections';

export const reduceCachedEntityDelta = <TEntity extends EntityWithId>(
    existing: TEntity | undefined,
    packet: DeltaPacket<string, Partial<TEntity>>,
): TEntity | undefined => {
    if (packet.operation === 'delete' || !packet.payload) {
        return undefined;
    }

    if (existing) {
        return mergeEntity(existing, { ...packet.payload, id: packet.entityId });
    }

    return packet.operation === 'create' ? ({ ...packet.payload, id: packet.entityId } as TEntity) : undefined;
};

export const reduceEntityDelta = <TEntity extends EntityWithId>(
    items: readonly TEntity[],
    selected: TEntity | null,
    packet: DeltaPacket<string, Partial<TEntity>>,
    options?: { prependCreated?: boolean },
) => {
    if (packet.operation === 'delete') {
        return {
            items: removeEntityById(items, packet.entityId),
            selected: selected?.id === packet.entityId ? null : selected,
        };
    }

    if (!packet.payload) {
        return null;
    }

    const existing = items.find((item) => item.id === packet.entityId);
    const nextEntity = existing
        ? ({ ...existing, ...packet.payload, id: packet.entityId } as TEntity)
        : packet.operation === 'create'
          ? ({ ...packet.payload, id: packet.entityId } as TEntity)
          : null;

    return {
        // Delta update payloads are intentionally partial. Merge them into the
        // canonical entity before upserting so unrelated identity, avatar, and
        // relationship fields are never discarded by an optimistic or realtime
        // update. Only create packets may introduce a previously unseen entity;
        // an isolated partial update must not create a poisoned list record.
        items: nextEntity
            ? upsertEntityById(items, nextEntity, {
                  prepend: Boolean(options?.prependCreated && !existing && packet.operation === 'create'),
              })
            : [...items],
        selected:
            selected?.id === packet.entityId
                ? mergeEntity(selected, { ...packet.payload, id: packet.entityId })
                : selected,
    };
};
